import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { motion } from 'framer-motion';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 1, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 }
  },
  viewport: { once: true }
};

const staggerItem = {
  initial: { opacity: 0, x: -20 },
  whileInView: { opacity: 1, x: 0 },
  transition: { duration: 0.5, ease: "easeOut" }
};

export const DivisionLayout = ({ 
  divisionName, 
  accentColor, 
  heroImage, 
  aboutText, 
  aboutImage,
  aboutImagePosition = 'center',
  galleryImages,
  capabilities,
  partnerLogo = null,
  partnerLogoUrl = null
}) => {
  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="relative h-[60vh] flex items-end overflow-hidden"
        data-testid={`${divisionName.toLowerCase().replace(' ', '-')}-hero`}
      >
        <motion.div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </motion.div>
        
        {/* Partner Logo - Top of hero, below navbar */}
        {partnerLogo && (
          <motion.div 
            className="absolute top-24 left-0 right-0 z-10"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
              {partnerLogoUrl ? (
                <a 
                  href={partnerLogoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block group"
                >
                  <img 
                    src={partnerLogo} 
                    alt="Partner Logo"
                    className="h-12 md:h-16 w-auto hover:opacity-80 transition-opacity"
                  />
                  <p className="text-xs tracking-widest uppercase text-white/50 group-hover:text-white/80 transition-colors flex items-center gap-1 mt-1">
                    <span className="group-hover:-translate-y-0.5 transition-transform inline-block">↑</span>
                    Spec Sheet
                  </p>
                </a>
              ) : (
                <img 
                  src={partnerLogo} 
                  alt="Partner Logo"
                  className="h-12 md:h-16 w-auto"
                />
              )}
            </div>
          </motion.div>
        )}
        
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 pb-12 md:pb-16 w-full">
          <motion.p 
            className="text-xs font-bold tracking-[0.2em] uppercase text-white/70 mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            CATWORTH
          </motion.p>
          
          <motion.h1 
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <span style={{ color: accentColor }}>{divisionName}</span>
          </motion.h1>
        </div>
      </section>

      {/* About Section */}
      <section className="pt-16 pb-12 md:pt-20 md:pb-16" data-testid={`${divisionName.toLowerCase().replace(' ', '-')}-about`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div {...fadeInUp}>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500 mb-4">
                Core Overview
              </p>
              <h2 
                className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-6"
                style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
              >
                {divisionName}
              </h2>
              <div className="text-lg text-neutral-600 leading-relaxed space-y-4">
                {Array.isArray(aboutText) ? (
                  aboutText.map((item, index) => (
                    <p key={index} dangerouslySetInnerHTML={{ __html: item.text }} />
                  ))
                ) : (
                  aboutText.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))
                )}
              </div>
            </motion.div>
            <motion.div 
              className="relative aspect-[4/3] overflow-hidden"
              {...fadeIn}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              <img 
                src={aboutImage} 
                alt={`${divisionName} manufacturing`}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                style={{ objectPosition: aboutImagePosition }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Capabilities Section - Image Left, Text Right */}
      <section className="pt-12 pb-16 md:pt-16 md:pb-20" data-testid={`${divisionName.toLowerCase().replace(' ', '-')}-capabilities`}>
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div 
              className="relative aspect-[4/3] overflow-hidden order-2 lg:order-1"
              {...fadeIn}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              <img 
                src={galleryImages[0]} 
                alt={`${divisionName} capabilities`}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </motion.div>
            <div className="order-1 lg:order-2">
              <motion.div {...fadeInUp}>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500 mb-4">
                  Key Capabilities
                </p>
                <h2 
                  className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-8"
                  style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
                >
                  What We Offer
                </h2>
              </motion.div>
              <motion.ul 
                className="space-y-4"
                {...staggerContainer}
              >
                {capabilities.map((capability, index) => (
                  <motion.li 
                    key={index} 
                    className="flex items-start gap-4"
                    variants={staggerItem}
                    initial="initial"
                    whileInView="whileInView"
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <span 
                      className="w-2 h-2 mt-2 flex-shrink-0"
                      style={{ backgroundColor: accentColor }}
                    />
                    {typeof capability === 'string' ? (
                      <span className="text-lg text-neutral-600">{capability}</span>
                    ) : (
                      <div>
                        <span className="text-lg font-semibold text-neutral-800">{capability.title}</span>
                        <p className="text-base text-neutral-600 mt-1">{capability.description}</p>
                      </div>
                    )}
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
